import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AgreementModal from "@/components/AgreementModal";
import ErrorBoundary from "@/components/ErrorBoundary";
import KeyboardShortcuts from "@/components/KeyboardShortcuts";
import NotFound from "@/pages/NotFound";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import DashboardLayout from "@/pages/DashboardLayout";
import Overview from "@/pages/Overview";
import Sources from "@/pages/Sources";
import Knowledge from "@/pages/Knowledge";
import MapPage from "@/pages/MapPage";
import Skills from "@/pages/Skills";
import Chat from "@/pages/Chat";
import LiveUpdates from "@/pages/LiveUpdates";
import Gaps from "@/pages/Gaps";
import ApiKeys from "@/pages/ApiKeys";
import Members from "@/pages/Members";
import Settings from "@/pages/Settings";
import Suggestions from "@/pages/Suggestions";
import Memory from "@/pages/Memory";
import Integrations from "@/pages/Integrations";
import Induction from "@/pages/Induction";
import Entities from "@/pages/Entities";
import Audit from "@/pages/Audit";
import Pricing from "@/pages/Pricing";
import Onboarding from "@/pages/Onboarding";
import NudgeDigestPreview from "@/pages/NudgeDigestPreview";
import SuperAdminRoutes from "@/pages/SuperAdmin";
import EnterpriseLeads from "@/pages/EnterpriseLeads";
import AcceptInvite from "@/pages/AcceptInvite";
import WorkLog from "@/pages/WorkLog";
import WorkIntelligenceDash from "@/pages/WorkIntelligenceDash";
import BotAccess from "@/pages/BotAccess";
import KnowledgeDecay from "@/pages/KnowledgeDecay";
import PeopleDirectory from "@/pages/PeopleDirectory";
import TemplateLibrary from "@/pages/TemplateLibrary";
import NudgeRuleLibrary from "@/pages/NudgeRuleLibrary";
import Billing from "@/pages/Billing";
import Meetings from "@/pages/Meetings";

function ProtectedRoute({ children }) {
  const { loading, user, agreementRequired } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading workspace…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      {children}
      {agreementRequired && <AgreementModal />}
    </>
  );
}

function AdminOnly({ children }) {
  const { loading, user, isAdmin } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { loading, user } = useAuth();
  const [params] = useSearchParams();
  if (loading) return null;
  if (user) {
    const next = params.get("next");
    return <Navigate to={next || "/app"} replace />;
  }
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster richColors position="top-right" />
            <KeyboardShortcuts />
            <Routes>
            <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/superadmin/*" element={<SuperAdminRoutes />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Overview />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="nudge-digest" element={<NudgeDigestPreview />} />
              <Route path="sources" element={<Sources />} />
              <Route path="knowledge" element={<Knowledge />} />
              <Route path="map" element={<MapPage />} />
              <Route path="skills" element={<Skills />} />
              <Route path="chat" element={<Chat />} />
              <Route path="updates" element={<LiveUpdates />} />
              <Route path="gaps" element={<Gaps />} />
              <Route path="suggestions" element={<Suggestions />} />
              <Route path="memory" element={<Memory />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="induction" element={<Induction />} />
              <Route path="entities" element={<Entities />} />
              <Route path="work-log" element={<WorkLog />} />
              <Route path="work-intelligence" element={<AdminOnly><WorkIntelligenceDash /></AdminOnly>} />
              <Route path="bot-access" element={<BotAccess />} />
              <Route path="people" element={<PeopleDirectory />} />
              <Route path="knowledge-decay" element={<KnowledgeDecay />} />
              <Route path="meetings" element={<Meetings />} />
              <Route path="template-library" element={<AdminOnly><TemplateLibrary /></AdminOnly>} />
              <Route path="nudge-library" element={<AdminOnly><NudgeRuleLibrary /></AdminOnly>} />
              <Route path="billing" element={<AdminOnly><Billing /></AdminOnly>} />
              <Route path="audit" element={<AdminOnly><Audit /></AdminOnly>} />
              <Route path="enterprise-leads" element={<AdminOnly><EnterpriseLeads /></AdminOnly>} />
              <Route path="agent" element={<AdminOnly><ApiKeys /></AdminOnly>} />
              <Route path="members" element={<AdminOnly><Members /></AdminOnly>} />
              <Route path="settings" element={<AdminOnly><Settings /></AdminOnly>} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
